import React from 'react'
import { css } from '@emotion/core'
import { useTranslation } from 'react-i18next'
import { DailySummary, MonthlySummary } from '@/models/sales-manager/detail'
import { DetailCard, DetailCardTitle } from '@/components/molecules/sales-manager/detail-card'

type SalesProps = {
  salesTotalDetail: DailySummary | MonthlySummary
  mode: 'daily' | 'month'
}

export const Sales: React.FC<SalesProps> = ({ salesTotalDetail, mode }) => {
  const { t } = useTranslation()

  return (
    <DetailCard>
      <DetailCardTitle>{t('Actual sales')}</DetailCardTitle>

      <div css={summaryBlockStyle}>
        <div className="summary-block">
          <div className="sub-title">{t('Room sales')}</div>
          {salesTotalDetail.sales.customerSummaries.map((detail, index) => (
            <div className="summary-line indent-text" key={`room-sales-${index}`}>
              <div>{detail.subjectName}</div>
              <div className="price-with-quantity">
                <div>{detail.quantity}</div>
                <div>¥{detail.price.toLocaleString()}</div>
              </div>
            </div>
          ))}
          <div className="subtotal-line indent-text">
            <div>{t('Room Sales Subtotal')}</div>
            <div>¥{salesTotalDetail.sales.customerTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="summary-block">
          <div className="sub-title">{t('Other sales')}</div>
          {salesTotalDetail.sales.otherSummaries.map((detail, index) => (
            <div className="summary-line indent-text" key={`other-sales-${index}`}>
              <div>{detail.subjectName}</div>
              <div className="price-with-quantity">
                <div>{detail.quantity}</div>
                <div>¥{detail.price.toLocaleString()}</div>
              </div>
            </div>
          ))}
          <div className="subtotal-line indent-text">
            <div>{t('Other Sales Subtotal')}</div>
            <div>¥{salesTotalDetail.sales.otherTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="summary-block">
          <div className="sub-title">{t('Markdown')}</div>
          {salesTotalDetail.sales.discountSummaries.map((detail, index) => (
            <div className="summary-line indent-text" key={`other-sales-${index}`}>
              <div>{detail.subjectName}</div>
              <div className="price-with-quantity">
                <div>{detail.quantity}</div>
                <div>¥{detail.price.toLocaleString()}</div>
              </div>
            </div>
          ))}
          <div className="subtotal-line indent-text">
            <div>{t('Total Markdown')}</div>
            <div>¥{salesTotalDetail.sales.discountTotal.toLocaleString()}</div>
          </div>
        </div>

        <div className="summary-block">
          <div className="total-line">
            <div>{t('Total')}</div>
            <div>¥{salesTotalDetail.sales.total.toLocaleString()}</div>
          </div>
          <div className="summary-line">
            <div>{t('Consumption tax')}</div>
            <div>¥{salesTotalDetail.sales.taxInclude.toLocaleString()}</div>
          </div>
        </div>

        <div className="summary-block">
          {salesTotalDetail.sales.temporarySummaries.map((temporary, i) => (
            <div className="summary-line" key={`temporary-${i}`}>
              <div>
                {t('Suspense receipt')}・{temporary.subjectName}
              </div>
              <div className="price-with-quantity">
                <div>{temporary.quantity}</div>
                <div>¥{temporary.price.toLocaleString()}</div>
              </div>
            </div>
          ))}
          {salesTotalDetail.sales.depositSummaries.map((deposit, i) => (
            <div className="summary-line" key={`deposit-${i}`}>
              <div>
                {t('Advance payment')}・{deposit.companyName}
              </div>
              <div>¥{deposit.price.toLocaleString()}</div>
            </div>
          ))}
        </div>
      </div>
    </DetailCard>
  )
}

const summaryBlockStyle = css({
  padding: '0 32px 24px 32px',
  '.summary-block': {
    marginTop: 18,
    ':first-of-type': { marginTop: 0 },
  },
  '.summary-line': {
    display: 'flex',
    justifyContent: 'space-between',
    marginTop: 12,
    fontSize: 14,
    ':first-of-type': { marginTop: 0 },
    '.price-with-quantity': {
      display: 'flex',
      '>div:last-of-type': { width: 120, textAlign: 'right' },
    },
  },
  '.indent-text': { textIndent: '1rem' },
  '.subtotal-line': {
    display: 'flex',
    justifyContent: 'space-between',
    fontWeight: 'bold',
    borderTop: '1px solid #f2f2f2',
    marginTop: 6,
    paddingTop: 6,
    fontSize: 14,
    textIndent: '1rem',
  },
  '.total-line': {
    display: 'flex',
    justifyContent: 'space-between',
    fontSize: 18,
    fontWeight: 'bold',
    borderTop: '2px solid #f2f2f2',
    paddingTop: 18,
  },
})
